import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { updateAttachment } from '../../../../actions/apiAttachments';
import { addMpiWorkItem } from '../../../../actions/apiMpiWorkItems';
import { addEstimate } from '../../../../actions/apiEstimates';
import { addWorkItemPartsBulk } from '../../../../actions/apiWorkItemParts';
import { presetPartsToItems } from '../../../../components/Parts/partsUtils';
import {
	dollarTabWorkItemSelectOptions,
	resolveDollarWorkItemsTab,
	workItemPatchFromValue,
	asAttachmentList,
	workItemCategoryColor,
} from './fileAttachmentWorkItems';

function isCreatedId(value) {
	return value != null && typeof value !== 'object';
}

function assertNoApiError(result) {
	if (result && result.errors) throw new Error('api_failed');
	return result;
}

class AttachToWorkItemDialog extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			tab: 'vehicle',
			search: '',
			saving: false,
			error: '',
		};
		this.onSelectExisting = this.onSelectExisting.bind(this);
		this.onSelectPreset = this.onSelectPreset.bind(this);
	}

	attachmentCountsByValue() {
		const counts = {};
		const list = asAttachmentList(
			this.props.active_vehicle && this.props.active_vehicle.attachments,
		);
		list.forEach((att) => {
			const key = (() => {
				if (att.mpi_work_item_id != null && att.mpi_work_item_id !== '') return `mpi:${att.mpi_work_item_id}`;
				if (att.appraisal_id != null && att.appraisal_id !== '') return `appraisal:${att.appraisal_id}`;
				if (att.work_item_id != null && att.work_item_id !== '') return `legacy:${att.work_item_id}`;
				return '';
			})();
			if (!key) return;
			counts[key] = (counts[key] || 0) + 1;
		});
		return counts;
	}

	vehicleOptions() {
		const vehicle = this.props.active_vehicle || {};
		return dollarTabWorkItemSelectOptions({
			allTabs: this.props.all_tabs,
			currentDealership: this.props.current_dealership,
			mpiWorkItems: [vehicle.mpi_work_items, this.props.mpi_work_items],
			appraisals: [vehicle.appraisals, this.props.appraisals],
			workItems: [vehicle.work_items, this.props.work_items],
			vehicleId: this.props.vehicle_id,
		});
	}

	dealershipPresets() {
		const presets = this.props.work_item_presets || [];
		const currentDealership = this.props.current_dealership;
		if (!currentDealership) return presets;
		return presets.filter((p) => {
			const dealershipField = (p.dealership || '').trim();
			if (!dealershipField) return true;
			return dealershipField.split(',').map(d => d.trim()).includes(currentDealership);
		});
	}

	filteredPresets() {
		const presets = this.dealershipPresets();
		const q = this.state.search.trim().toLowerCase();
		if (!q) return presets;
		return presets.filter((p) => {
			const name = String(p.name || p.description || '').toLowerCase();
			const category = String(p.category || '').toLowerCase();
			return name.includes(q) || category.includes(q);
		});
	}

	defaultLaborRate() {
		const subdomain = this.props.installation && this.props.installation.subdomain;
		const settings = this.props.dealer_settings || [];
		const match = settings.find(o => o.key === 'default_labor_rate' && o.dealership === subdomain);
		return match && match.value != null && match.value !== '' ? Number(match.value) : 100;
	}

	createsAppraisalsFromCatalog() {
		const tab = resolveDollarWorkItemsTab(this.props.all_tabs, this.props.current_dealership);
		const name = tab && tab.name;
		// Buckhead $ tab and classic estimate tabs create appraisals from presets.
		return !name || name === 'tab_new_work_items' || name === 'tab_estimate';
	}

	linkAttachment(patch) {
		return Promise.resolve(this.props.updateAttachment(
			this.props.attachment.id,
			patch,
			'updateAttachmentError',
		)).then(assertNoApiError);
	}

	addPresetParts(preset, basePayload) {
		const fullPreset = (this.props.work_item_presets || [])
			.find(p => String(p.id) === String(preset.id)) || preset;
		const uid = parseInt(this.props.current_user && this.props.current_user.user, 10) || null;
		const subdomain = this.props.installation && this.props.installation.subdomain;
		const items = presetPartsToItems(
			fullPreset,
			this.props.catalog_parts,
			{ ...basePayload, dealership: subdomain },
			uid,
		);
		if (items.length) {
			this.props.addWorkItemPartsBulk(items, basePayload.vehicle_id);
		}
	}

	onSelectExisting(value) {
		const vehicle_id = this.props.vehicle_id;
		this.setState({ saving: true, error: '' });
		this.linkAttachment({
			vehicle_id,
			...workItemPatchFromValue(value),
		}).then(() => {
			this.setState({ saving: false });
			this.props.onAttached();
		}).catch(() => {
			this.setState({ saving: false, error: 'Could not attach file.' });
		});
	}

	createFromPreset(preset) {
		const vehicle_id = this.props.vehicle_id;
		const name = preset.name || preset.description || `Work Item #${preset.id}`;
		const subdomain = this.props.installation && this.props.installation.subdomain;

		if (this.createsAppraisalsFromCatalog()) {
			const defaultLabor = this.defaultLaborRate();
			const labor_price = Number(preset.labor_price) > 0 ? Number(preset.labor_price) : defaultLabor;
			const labor_units = Number(preset.labor_units) || 0;
			const parts_price = Number(preset.parts_price) || 0;
			const labor_total = labor_units * labor_price;
			const estimate_total = parts_price + labor_total;
			const lock = preset.name !== 'Blank Row';
			return Promise.resolve(this.props.addEstimate({
				vehicle_id,
				ro_status: 'pending',
				description: preset.description || name,
				label_item_1: preset.name || name,
				category: preset.category || '',
				estimate_type: preset.estimate_type,
				parts_price,
				labor_units,
				labor_price,
				estimate_total,
				estimate: estimate_total,
				lock_estimate: lock,
				lock_item_1: preset.parts_price_editable,
				lock_item_2: preset.labor_units_editable,
				lock_item_3: preset.labor_price_editable,
				lock_item_4: preset.estimate_total_editable,
			}, 'addEstimateError')).then((newId) => {
				if (!isCreatedId(newId)) throw new Error('create_failed');
				this.addPresetParts(preset, {
					vehicle_id,
					work_item_source: 'appraisals',
					appraisal_id: newId,
				});
				return this.linkAttachment({
					vehicle_id,
					appraisal_id: newId,
					mpi_work_item_id: null,
					work_item_id: null,
				});
			});
		}

		const laborRate = Number(preset.labor_price) > 0 ? Number(preset.labor_price) : this.defaultLaborRate();
		const parts = Number(preset.parts_price) || 0;
		const laborHours = Number(preset.labor_units) || 0;
		const laborTotal = laborHours * laborRate;
		return Promise.resolve(this.props.addMpiWorkItem({
			vehicle_id,
			include_in_work_items: true,
			ro_status: 'pending',
			category: preset.category || 'mechanical',
			name,
			description: preset.description || name,
			work_item_preset_id: preset.id,
			parts,
			parts_total: parts,
			quantity: 1,
			labor_hours: laborHours,
			labor_rate: laborRate,
			labor_total: laborTotal,
			estimate_total: parts + laborTotal,
			estimate_type: preset.estimate_type,
			layout_type: 'default',
			active: true,
			deleted: false,
			dealership: subdomain,
		}, 'addWorkItemError')).then((newId) => {
			if (!isCreatedId(newId)) throw new Error('create_failed');
			this.addPresetParts(preset, {
				vehicle_id,
				work_item_source: 'new_work_items',
				mpi_work_item_id: newId,
			});
			return this.linkAttachment({
				vehicle_id,
				mpi_work_item_id: newId,
				appraisal_id: null,
				work_item_id: null,
			});
		});
	}

	onSelectPreset(preset) {
		this.setState({ saving: true, error: '' });
		this.createFromPreset(preset).then(() => {
			this.setState({ saving: false });
			this.props.onAttached();
		}).catch(() => {
			this.setState({ saving: false, error: 'Could not create and attach work item.' });
		});
	}

	hasPresets() {
		return this.dealershipPresets().length > 0;
	}

	activeTab() {
		if (this.state.tab === 'catalog' && !this.hasPresets()) return 'vehicle';
		return this.state.tab;
	}

	renderVehicleRows() {
		const counts = this.attachmentCountsByValue();
		const vehicleOpts = this.vehicleOptions();
		if (vehicleOpts.length === 0) {
			return <div className="empty-state">No work items on this vehicle yet.</div>;
		}
		return (
			<div className="wi-list">
				{vehicleOpts.map(opt => (
					<button
						key={opt.value}
						type="button"
						className="wi-row"
						disabled={this.state.saving}
						onClick={() => this.onSelectExisting(opt.value)}
					>
						<div className="l">
							<span className="dot" style={{ background: workItemCategoryColor(opt.category) }} />
							<div>
								<div className="name">{opt.label}</div>
								{opt.description ? <div className="desc">{opt.description}</div> : null}
								<div className="meta">{counts[opt.value] || 0} file(s) attached</div>
							</div>
						</div>
						<span className="select-btn">Select</span>
					</button>
				))}
			</div>
		);
	}

	renderCatalogRows() {
		const presets = this.filteredPresets();
		return (
			<div>
				<input
					type="search"
					className="search-input2"
					placeholder="Search the catalog"
					value={this.state.search}
					onChange={e => this.setState({ search: e.target.value })}
					disabled={this.state.saving}
				/>
				{presets.length === 0 ? (
					<div className="empty-state">No matching work items.</div>
				) : (
					<div className="wi-list">
						{presets.map(preset => (
							<button
								key={`preset_${preset.id}`}
								type="button"
								className="wi-row"
								disabled={this.state.saving}
								onClick={() => this.onSelectPreset(preset)}
							>
								<div className="l">
									<span className="dot" style={{ background: workItemCategoryColor(preset.category) }} />
									<div>
										<div className="name">{preset.name || preset.description || `Preset #${preset.id}`}</div>
										{preset.description && preset.name ? (
											<div className="desc">{preset.description}</div>
										) : null}
									</div>
								</div>
								<span className="select-btn">Select</span>
							</button>
						))}
					</div>
				)}
			</div>
		);
	}

	render() {
		const showCatalog = this.hasPresets();
		const tab = this.activeTab();
		const fileName = (this.props.attachment && this.props.attachment.fileid) || '';

		return (
			<div
				className="files-attach-backdrop"
				onClick={(e) => {
					if (e.target === e.currentTarget && !this.state.saving) this.props.onClose();
				}}
				role="presentation"
			>
				<div
					className="files-attach-modal"
					role="dialog"
					aria-modal="true"
					aria-label="Attach to Work Item"
					onClick={e => e.stopPropagation()}
				>
					<div className="modal-head">
						<div>
							<div className="t">Attach to Work Item</div>
							<div className="s">{fileName}</div>
						</div>
						<button
							type="button"
							className="icon-btn"
							aria-label="Close"
							onClick={this.props.onClose}
							disabled={this.state.saving}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
								<line x1="4" y1="4" x2="20" y2="20" />
								<line x1="20" y1="4" x2="4" y2="20" />
							</svg>
						</button>
					</div>

					{showCatalog && (
						<div className="modal-tabs">
							<button
								type="button"
								className={`modal-tab ${tab === 'vehicle' ? 'active' : ''}`}
								onClick={() => this.setState({ tab: 'vehicle' })}
							>
								Work Items on This Vehicle
							</button>
							<button
								type="button"
								className={`modal-tab ${tab === 'catalog' ? 'active' : ''}`}
								onClick={() => this.setState({ tab: 'catalog' })}
							>
								All Work Items
							</button>
						</div>
					)}

					<div className="modal-body">
						{this.state.error && <div className="attach-error">{this.state.error}</div>}
						{this.state.saving && <div className="attach-saving">Saving…</div>}
						{tab === 'vehicle' && this.renderVehicleRows()}
						{tab === 'catalog' && showCatalog && this.renderCatalogRows()}
					</div>

					<div className="modal-footer">
						<button
							type="button"
							className="btn-outline"
							onClick={this.props.onClose}
							disabled={this.state.saving}
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		);
	}
}

const mappedActions = {
	updateAttachment,
	addMpiWorkItem,
	addEstimate,
	addWorkItemPartsBulk,
};

function mapStateToProps(state) {
	return {
		active_vehicle: state.settings_refresh.active_vehicle,
		mpi_work_items: state.settings_refresh.mpi_work_items,
		appraisals: state.settings_refresh.appraisals,
		work_items: state.settings_refresh.work_items,
		work_item_presets: state.settings_refresh.work_item_presets,
		catalog_parts: state.settings_refresh.catalog_parts || [],
		all_tabs: state.settings_site.all_tabs,
		current_dealership: state.settings_dealerships.current_dealership,
		dealer_settings: state.settings_site.dealer_settings,
		installation: state.settings_dealerships.installation,
		current_user: state.current_user,
	};
}

export default connect(mapStateToProps, mappedActions)(AttachToWorkItemDialog);
